/**
 * Pre-compiles the decode-worker WASM image codecs at app start.
 *
 * The DICOM decode workers spawn eagerly at init, but each worker fetches and
 * compiles a codec's WASM module lazily on its FIRST decode of that transfer
 * syntax (module-level `local.codec` cache in @cornerstonejs/dicom-image-loader's
 * shared decoders). Without warm-up that fetch+compile lands on the first
 * radiograph a radiologist opens. Decoding one tiny embedded codestream per
 * worker per codec moves the cost to app start instead.
 *
 * Reaching EVERY worker: the worker manager's least-load selection increments a
 * per-worker load counter synchronously as each task is dispatched (compute-type
 * requests are effectively unthrottled), so dispatching `workerCount` concurrent
 * tasks per codec spreads them one per worker.
 *
 * The embedded frames are genuinely valid codestreams (decode-verified against
 * the exact charls/openjpeg decode WASM builds the worker uses) rather than
 * garbage bytes: the decoders reuse one module-level decoder instance per
 * worker, and feeding it a corrupt stream risks poisoning state shared with
 * real decodes.
 */
import { getWebWorkerManager } from '@cornerstonejs/core';
const WARMUP_TARGETS = [
    {
        // JPEG-LS Lossless -> charls (what ingest stores for every eligible image)
        transferSyntax: '1.2.840.10008.1.2.4.80',
        rows: 1,
        columns: 1,
        base64: '/9j/9wALCAABAAEBAREA/9oACAEBAAAAAAAAAf3/2Q==',
    },
    {
        // JPEG 2000 Lossless -> openjpeg (images already in the archive)
        // 16x16: the openjpeg encoder's minimum frame at its default decompositions.
        transferSyntax: '1.2.840.10008.1.2.4.90',
        rows: 16,
        columns: 16,
        base64: '/0//UQApAAAAAAAQAAAAEAAAAAAAAAAAAAAAEAAAABAAAAAAAAAAAAABBwEB/1IADAACAAEABAQEAAH/XAAQQEBISFBISFBISFBISFD/ZAAEAAH/kAAKAAAAAAATAAH/k4CAgICA/9k=',
    },
];
function toBytes(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++)
        bytes[i] = binary.charCodeAt(i);
    return bytes;
}
/**
 * Fire one tiny decode per codec into each of the `workerCount` decode workers.
 * Best-effort: a rejected warm-up task only means that worker pays the codec
 * compile on its first real frame, so failures are swallowed. The returned
 * promise (which never rejects) is for tests; production callers fire-and-forget.
 */
export function warmUpDicomCodecs(workerCount) {
    const workerManager = getWebWorkerManager();
    const tasks = [];
    for (const target of WARMUP_TARGETS) {
        const pixelData = toBytes(target.base64);
        for (let i = 0; i < workerCount; i++) {
            tasks.push(workerManager
                .executeTask('dicomImageLoader', 'decodeTask', {
                imageFrame: {
                    samplesPerPixel: 1,
                    photometricInterpretation: 'MONOCHROME2',
                    rows: target.rows,
                    columns: target.columns,
                    bitsAllocated: 8,
                    bitsStored: 8,
                    highBit: 7,
                    pixelRepresentation: 0,
                },
                transferSyntax: target.transferSyntax,
                pixelData,
                decodeConfig: {},
                // preScale must be present and disabled: the worker's post-processing
                // dereferences options.preScale.enabled unconditionally.
                options: { preScale: { enabled: false } },
            })
                .catch(() => { }));
        }
    }
    return Promise.all(tasks).then(() => undefined);
}
